import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  lotCardBodyRecipe,
  lotCardCornerStartRecipe,
  lotCardRecipe,
} from '../recipes/lot-card.recipe';
import { Icon } from './Icon';
import { Inline, Stack } from './Layout';
import { MediaFrame } from './Media';
import { Text } from './Text';

export interface LotCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** The first photo. Nothing draws the "no picture" frame. */
  src?: string | null;
  alt?: string;
  title: React.ReactNode;
  /** The line under the title — category and mileage, or buyer and date. */
  meta?: React.ReactNode;
  /** A glyph in front of `meta`: the category icon. */
  metaIcon?: LucideIcon;
  /** The kebab menu. It should stop propagation so it does not open the lot. */
  actions?: React.ReactNode;
  price: React.ReactNode;
  /** Beside the price: "Starting bid", or the listed price it sold under. */
  priceNote?: React.ReactNode;
  /** Top-right of the picture: the status badge. */
  corner?: React.ReactNode;
  /** Top-left of the picture: the lot number. */
  cornerStart?: React.ReactNode;
  /** An archived lot: faded, with its photo in greyscale. */
  muted?: boolean;
  /** Click, Enter or Space anywhere on the card. */
  onSelect?: () => void;
}

/**
 * One lot on the auction grid.
 *
 *   <LotCard
 *     src={lot.photos[0]} alt={lot.title}
 *     title={lot.title} meta="Vehicle · 84,000 mi" metaIcon={Car}
 *     price="$4,200" priceNote="Starting bid"
 *     corner={<Badge tone="warning">Archived</Badge>}
 *     actions={<LotMenu lot={lot} />}
 *     onSelect={() => openDetail(lot)}
 *   />
 *
 * The card is a button to the keyboard — role, tab stop, Enter and Space —
 * so the page only says what selecting it does.
 */
export const LotCard = React.forwardRef<HTMLDivElement, LotCardProps>(
  function LotCard(
    {
      src,
      alt = '',
      title,
      meta,
      metaIcon,
      actions,
      price,
      priceNote,
      corner,
      cornerStart,
      muted = false,
      onSelect,
      onClick,
      onKeyDown,
      className,
      ...props
    },
    ref
  ) {
    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      onSelect?.();
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect?.();
      }
    };

    return (
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        className={cn(lotCardRecipe({ interactive: true, muted }), className)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...props}
      >
        <MediaFrame
          src={src}
          alt={alt}
          ratio="4/3"
          imageClassName={muted ? 'grayscale' : undefined}
          corner={corner}
        >
          {cornerStart ? (
            <span className={lotCardCornerStartRecipe()}>{cornerStart}</span>
          ) : null}
        </MediaFrame>
        <div className={lotCardBodyRecipe()}>
          <Inline gap={2} align="start" justify="between">
            <Stack gap={0} grow>
              <Text variant="body-strong" truncate>
                {title}
              </Text>
              {meta ? (
                <Inline gap={1} className="min-w-0">
                  {metaIcon ? (
                    <Icon icon={metaIcon} size="xs" tone="muted" />
                  ) : null}
                  <Text variant="caption" tone="muted" truncate>
                    {meta}
                  </Text>
                </Inline>
              ) : null}
            </Stack>
            {actions}
          </Inline>
          <Inline gap={0} align="baseline" justify="between">
            <Text variant="h4" as="span" numeric>
              {price}
            </Text>
            {priceNote ? (
              <Text variant="caption" tone="muted" as="span">
                {priceNote}
              </Text>
            ) : null}
          </Inline>
        </div>
      </div>
    );
  }
);
